/**
 * Creates an event hub object.
 *
 * This is a port to ES6 of the [dush](https://github.com/tunnckoCoreLabs/dush) library.
 *
 * @class
 * @example
 * import { Events } from 'yuzu-utils';
 *
 * const events = new Events();
 *
 * events.on('log', (msg) => console.log(msg))
 *
 * events.emit('log', 'Hello world!') // logs 'Hello world!'
 */
var Events =
/*#__PURE__*/
function () {
  function Events() {
    this._allEvents = Object.create(null);
  }
  /**
   * ```js
   * this.on(event, handler [, once])
   * ```
   * Adds an event handler for an event.
   *
   * @param  {string} name Event name to listen for, or `'*'` for all events
   * @param  {Function} handler Function to call
   * @param  {boolean} [once] Make `handler` be called only once, the `.once` method use this internally
   * @return {Event}
   * @example
   * const emitter = new Events()
   *
   * emitter
   *   .on('hi', (place) => {
   *     console.log(`hello ${place}!`) // => 'hello world!'
   *   })
   *   .on('hi', (place) => {
   *     console.log(`hi ${place}, yeah!`) // => 'hi world, yeah!'
   *   })
   *
   * emitter.emit('hi', 'world')
   */


  var _proto = Events.prototype;

  _proto.on = function on(name, handler, once) {
    var _this = this;

    var e = this._allEvents[name] || (this._allEvents[name] = []);

    var func = function func() {
      _this.off(name, func);

      handler.apply(void 0, arguments);
    };

    func.fn = handler;
    var fn = once ? func : handler;
    e.push(fn);
    return this;
  }
  /**
   * ```js
   * this.once(event, handler)
   * ```
   *
   * Like `.on` but calls the handler just once.
   *
   * @param  {string} name Event name to listen for, or `'*'` for all events
   * @param  {function} handler Function to call
   * @return {Events}
   * @example
   * const emitter = new Events()
   * let called = 0
   *
   * emitter.once('foo', () => {
   *   console.log('called only once')
   *   called++
   * })
   *
   * emitter.emit('foo')
   * emitter.emit('foo')
   * emitter.emit('foo')
   *
   * console.log(called) // => 1
   */
  ;

  _proto.once = function once(name, handler) {
    this.on(name, handler, true);
    return this;
  }
  /**
   * ```js
   * this.off([event [, handler]])
   * ```
   *
   * Removes an event listener for `name` event. If `handler` is not specified it will remove **all** listeners for that `name` event.
   * If `name` is not specified as well, it will then remove all registered event handlers.
   *
   * @param {string} [name] Event name
   * @param {function} [handler] Handler to remove
   * @return {Events}
   * @example
   * const emitter = new Events()
   *
   * const handler = () => {
   *   console.log('not called')
   * }
   *
   * emitter.on('foo', handler)
   *
   * // remove just an handler
   * emitter.off('foo', handler)
   *
   * // remove all listeners for `foo`
   * emitter.off('foo')
   *
   * // remove all listeners
   * emitter.off()
   */
  ;

  _proto.off = function off(name, handler) {
    if (name && handler && this._allEvents[name]) {
      this._allEvents[name] = this._allEvents[name].filter(function (func) {
        return func !== handler && func !== handler.fn;
      });
    } else if (name) {
      this._allEvents[name] = [];
    } else {
      this._allEvents = Object.create(null);
    }

    return this;
  }
  /**
   * ```js
   * this.emit(event [, ...args])
   * ```
   *
   * Emits an event with optional parameters.
   * Will call every handler registered for the wildcard event `'*'` as well
   *
   * @param {string} name The name of the event to invoke
   * @param {...*} args Any number of arguments of any type of value, passed to each listener
   * @return {Events}
   * @example
   * const emitter = new Events()
   *
   * emitter.on('foo', (a, b, c) => {
   *   console.log(`${a}, ${b}, ${c}`) // => 1, 2, 3
   * })
   *
   * emitter.on('*', (name, a, b, c) => {
   *   console.log(`event name is: ${name}`)
   *   console.log(`args are: ${a}, ${b}, ${c}`)
   * })
   *
   * emitter.emit('foo', 1, 2, 3)
   * emitter.emit('bar', 555)
   */
  ;

  _proto.emit = function emit(name) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    var _allEvents = this._allEvents;

    if (name !== '*') {
      if (_allEvents[name]) {
        _allEvents[name].map(function (handler) {
          handler.apply(void 0, args);
        });
      }

      if (_allEvents['*']) {
        _allEvents['*'].map(function (handler) {
          handler.apply(void 0, [name].concat(args));
        });
      }
    }

    return this;
  };

  return Events;
}();

/**
 * Event emitter constructor.
 *
 * [Documentation](packages/yuzu-utils/api/events)
 *
 * @name Events
 * @class
 *
 */

/**
 * uid prefix.
 *
 * @public
 * @type {string}
 */

var UID_PREFIX = '_ui.';
/**
 * Void function.
 *
 * @example
 * @type {function}
 * @example
 * import { noop } from 'yuzu-utils';
 *
 * noop() === undefined
 */

var noop = function noop() {};
var createSequence = function createSequence(start) {
  if (start === void 0) {
    start = -1;
  }

  var uid = start;
  var uidNs = '';
  return function (prefix) {
    if (prefix === void 0) {
      prefix = UID_PREFIX;
    }

    uid += 1;

    if (uid === Number.MAX_SAFE_INTEGER) {
      uid = 0;
      uidNs += '0';
    }

    return prefix + uidNs + uid;
  };
};
/**
 * Returns a sequential uid with optional prefix.
 *
 * @param {string} [prefix=UID_PREFIX] uid prefix. Defaults to the value of `UID_PREFIX`
 * @param {string} [seq] Numeric variable to use. Defaults to an internal sequence
 * @returns {string}
 * @example
 * import { nextUid } from 'yuzu-utils';
 *
 * nextUid() === '_ui.0'
 * nextUid() === '_ui.1'
 * nextUid('custom.') === 'custom.2'
 * nextUid(UID_PREFIX, 10) === '_ui.1'
 */

var nextUid =
/*#__PURE__*/
createSequence();
var funcToString = Function.prototype.toString;
var objProto = Object.prototype;
var objToString = objProto.toString;
var hasOwnProperty = objProto.hasOwnProperty;
var objectCtorString =
/*#__PURE__*/
funcToString.call(Object);
/**
 * Checks if a passed-in value has a `typeof` of `object`.
 *
 * @param {*} value Value to check
 * @returns {boolean}
 * @example
 * import { isObjectLike } from 'yuzu-utils';
 *
 * isObjectLike({}) === true
 * isObjectLike(false) === false
 * isObjectLike([]) === true
 */

var isObjectLike = function isObjectLike(value) {
  return !!value && typeof value === 'object';
};
/**
 * Checks if a value is a plain object (aka: _POJO_).
 *
 * @param {*} value Value to check
 * @returns {boolean}
 * @example
 * import { isPlainObject } from 'yuzu-utils';
 *
 * isPlainObject({}) === true
 * isPlainObject([]) === false
 */

var isPlainObject = function isPlainObject(value) {
  if (!isObjectLike(value) || objToString.call(value) !== '[object Object]') {
    return false;
  }

  var proto = Object.getPrototypeOf(value);

  if (proto === null) {
    return true;
  }

  var Ctor = hasOwnProperty.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor === 'function' && Ctor instanceof Ctor && funcToString.call(Ctor) === objectCtorString;
};
/**
 * Checks if a value is a DOM element.
 *
 * @param {*} value Value to check
 * @returns {boolean}
 * @example
 * import { isElement } from 'yuzu-utils';
 *
 * isPlainObject(document.body) === true
 * isPlainObject([]) === false
 */

var isElement = function isElement(value) {
  return !!value && value.nodeType === 1 && isObjectLike(value) && !isPlainObject(value);
};
/**
 * If `value` is a function executes it with passed-in arguments and returns its result,
 * otherwise returns `value`.
 *
 * @param {any} value
 * @param {...args} args Optional arguments
 * @return {*}
 * @example
 * import { evaluate } from 'yuzu-utils';
 *
 * const yesNo = (v) => v ? 'yes' : 'no';
 *
 * evaluate(yesNo, false) === 'no'
 * evaluate(yesNo, true) === 'yes'
 * evaluate(true) === true
 */

var evaluate = function evaluate(value) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }

  return typeof value === 'function' ? value.apply(void 0, args) : value;
};
/**
 * Binds the `this` context of a function to a passed-in object.
 *
 * If `method` is a string it will try to resolve the function as a member of the context object.
 *
 * @param {object} ctx Context
 * @param {string|function} method Function to bind
 * @example
 * import { evaluate } from 'yuzu-utils';
 *
 * const user = {
 *   name: 'John',
 *   surname: 'Doe',
 *   toLower() {
 *    this.name.toLowerCase();
 *   }
 * };
 *
 * const userMethods = {
 *
 * };
 *
 * function fullName () {
 *   return `${this.name} ${this.surname}`;
 * }
 *
 * const boundFullName = bindMethod(user, fullName);
 * boundFullName() === 'John Doe'
 *
 * const boundMethod = bindMethod(user, 'toLower');
 * boundMethod() === 'john'
 */

var bindMethod = function bindMethod(ctx, method) {
  if (typeof method === 'string') {
    if (typeof ctx[method] === 'function') {
      return function () {
        return ctx[method].apply(ctx, arguments);
      };
    }

    throw new TypeError("Property " + method + " should be a function");
  }

  return method.bind(ctx);
};
/**
 * Accepts a value and tries to parse it as boolean, number or JSON.
 *
 * @name parseString
 * @function
 * @private
 * @param {any} value - Value to parse
 * @returns {*}
 * @example
 * import { parseString } from 'yuzu-utils';
 *
 * parseString('true') === true
 * parseString('1') === 1
 * parseString('{ "name": "John" }') === { name: 'John' }
 * parseString('    John Doe   ') === 'John Doe'
 */

var parseString = function parseString(value) {
  if (typeof value !== 'string') {
    return undefined;
  }

  var v = value.trim();

  if (v === '' || v === 'true') {
    return true;
  }

  if (v === 'false') {
    return false;
  }

  var n = Number(v);

  if (!Number.isNaN(n)) {
    return n;
  }

  try {
    return JSON.parse(v);
  } catch (e) {
    return v;
  }
};
var INLINE_STATE_REGEXP = /^ui([A-Z].+)$/;
/**
 * Parses an element's [`dataset`](https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/dataset) with optional filtering.
 *
 * @param {HTMLElement} el HTML element
 * @param {RegExp} [matcher] Optional regexp to filter dataset by key (defaults to `/^ui([A-Z].+)$/`)
 * @param {function} [formatter=parseString] Optional formatter function.
 * @returns {object}
 * @example
 *
 * // html:
 * // <div id="demo" data-ui-bool data-ui-dashed-value="John">
 *
 * import { datasetParser, qs } from 'yuzu-utils';
 *
 * const data = datasetParser(qs('#demo'));
 *
 * data.bool === true
 * data.dashedValue === 'John'
 */

var datasetParser = function datasetParser(el, matcher, formatter) {
  if (matcher === void 0) {
    matcher = INLINE_STATE_REGEXP;
  }

  if (formatter === void 0) {
    formatter = parseString;
  }

  return Object.entries(el.dataset).reduce(function (acc, _ref) {
    var key = _ref[0],
        value = _ref[1];

    if (matcher.test(key)) {
      var newKey = key.replace(matcher, function (_, m) {
        return "" + m[0].toLowerCase() + m.slice(1);
      });
      acc[newKey] = formatter(value);
    }

    return acc;
  }, {});
};
var warn = function warn(ctx) {
  var name = ctx.constructor.displayName || ctx.constructor.name || 'Component';
  var _ctx$$uid = ctx.$uid,
      $uid = _ctx$$uid === void 0 ? 'unmounted' : _ctx$$uid;
  return function (msg) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }

    return console.warn("[yuzu:" + name + "#" + $uid + "] " + msg, args);
  };
};
/**
 * Returns the first element within the document that matches the specified group of selectors.
 *
 * @name qs
 * @function
 * @param {string} selector - CSS selector
 * @param {Element|Document} [ctx=document] - Root element. `document` by default
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/querySelector
 * @return {Element|null}
 * @example
 * import { qs } from 'yuzu-utils';
 *
 * const content = qs('#main-content');
 */

var qs = function qs(selector, ctx) {
  if (ctx === void 0) {
    ctx = document;
  }

  return ctx.querySelector(selector);
};
/**
 * Returns an array of elements within the document that match the specified group of selectors.
 *
 * @name qsa
 * @function
 * @param {string} selector - One or more CSS selectors separated by commas.
 * @param {Element|Document} [ctx=document] - Root element. `document` by default
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/querySelectorAll
 * @return {Element[]}
 * @example
 * import { qsa } from 'yuzu-utils';
 *
 * const listItems = qsa('.list .list-items');
 */

var qsa = function qsa(selector, ctx) {
  if (ctx === void 0) {
    ctx = document;
  }

  return Array.from(ctx.querySelectorAll(selector));
};

export { Events, INLINE_STATE_REGEXP, UID_PREFIX, bindMethod, createSequence, datasetParser, evaluate, isElement, isObjectLike, isPlainObject, nextUid, noop, parseString, qs, qsa, warn };
//# sourceMappingURL=yuzu-utils.esm.js.map
